/**
 * Central route registry.
 *
 * Every entry maps a URL path to (1) the React element to render and (2) the
 * <head> metadata (title, description, canonical, JSON-LD). It is the single
 * source of truth for the router (App.tsx / entry-server.tsx), the prerender
 * loop and the sitemap.
 */
import type { RouteHead } from "./head";
import { Home } from "./pages/Home";
import { RatgeberHub } from "./pages/RatgeberHub";
import { GuideArticle } from "./pages/GuideArticle";
import { Warnkarte } from "./pages/Warnkarte";
import { Impressum } from "./pages/Impressum";
import { Datenschutz } from "./pages/Datenschutz";
import { Agb } from "./pages/Agb";
import { faqs } from "./data/content";
import { AUTHOR, guides, ratgeberHub, SITE_ORIGIN, type Guide } from "./data/guides";

export interface RouteDef {
  path: string;
  element: JSX.Element;
  head: RouteHead;
  /** Include in sitemap.xml (default true). */
  sitemap?: boolean;
}

const abs = (p: string) => `${SITE_ORIGIN}${p}`;

const ORGANIZATION = {
  "@type": "Organization",
  name: "Welope",
  url: "https://welope.de/",
  logo: `${SITE_ORIGIN}/images/logo-512.png`,
};

/** Real named author (not the Organization) — see AUTHOR in data/guides.ts for why. */
const AUTHOR_PERSON = {
  "@type": "Person",
  name: AUTHOR.name,
  jobTitle: AUTHOR.role,
};

/** Build a schema.org FAQPage from question/answer pairs. */
function faqPage(items: ReadonlyArray<{ question: string; answer: string }>) {
  return {
    "@type": "FAQPage",
    mainEntity: items.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
}

function guideRoute(guide: Guide): RouteDef {
  const canonical = abs(`/ratgeber/${guide.slug}`);
  return {
    path: `/ratgeber/${guide.slug}`,
    element: <GuideArticle guide={guide} />,
    head: {
      title: `${guide.metaTitle} | WeLovePets`,
      description: guide.description,
      canonical,
      ogType: "article",
      jsonLd: [
        {
          "@type": "Article",
          headline: guide.metaTitle,
          description: guide.description,
          inLanguage: "de-DE",
          datePublished: guide.datePublished,
          dateModified: guide.dateModified ?? guide.datePublished,
          image: `${SITE_ORIGIN}${guide.heroImage.src}`,
          author: AUTHOR_PERSON,
          publisher: ORGANIZATION,
          mainEntityOfPage: canonical,
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Start", item: abs("/") },
            { "@type": "ListItem", position: 2, name: "Ratgeber", item: abs("/ratgeber") },
            { "@type": "ListItem", position: 3, name: guide.title, item: canonical },
          ],
        },
        faqPage(guide.faqs),
        ...(guide.howTo
          ? [
              {
                "@type": "HowTo",
                name: guide.howTo.name,
                step: guide.howTo.steps.map((s) => ({
                  "@type": "HowToStep",
                  name: s.name,
                  text: s.text,
                })),
              },
            ]
          : []),
      ],
    },
  };
}

export const routes: RouteDef[] = [
  {
    path: "/",
    element: <Home />,
    head: {
      title: "WeLovePets — Das Zuhause deines Lieblings im Internet",
      description:
        "Welope — das soziale Netzwerk für Haustierbesitzer: Profile für Hunde & Katzen, Feed, Gruppen und Chats. Self-hosted in Deutschland, DSGVO-konform.",
      canonical: abs("/"),
      ogType: "website",
      jsonLd: [
        {
          "@type": "WebSite",
          name: "Welope",
          url: abs("/"),
          description:
            "Das soziale Netzwerk für Haustierbesitzer — self-hosted in Deutschland, DSGVO-konform.",
          inLanguage: "de-DE",
          publisher: {
            "@type": "Organization",
            name: "Welope",
            slogan: "WeLovePets",
            legalName: "Jamie Hennemann",
            url: "https://welope.de/",
            logo: `${SITE_ORIGIN}/images/logo-512.png`,
            email: "info@solutioit.de",
            founder: { "@type": "Person", name: "Jamie Hennemann" },
            address: {
              "@type": "PostalAddress",
              postalCode: "56479",
              addressLocality: "Liebenscheid",
              addressRegion: "Rheinland-Pfalz",
              addressCountry: "DE",
            },
          },
        },
        faqPage(faqs),
      ],
    },
  },
  {
    path: "/ratgeber",
    element: <RatgeberHub />,
    head: {
      title: `${ratgeberHub.metaTitle} | WeLovePets`,
      description: ratgeberHub.description,
      canonical: abs("/ratgeber"),
      ogType: "website",
      jsonLd: [
        {
          "@type": "CollectionPage",
          name: ratgeberHub.metaTitle,
          description: ratgeberHub.description,
          inLanguage: "de-DE",
          url: abs("/ratgeber"),
          isPartOf: { "@type": "WebSite", name: "Welope", url: abs("/") },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Start", item: abs("/") },
            { "@type": "ListItem", position: 2, name: "Ratgeber", item: abs("/ratgeber") },
          ],
        },
      ],
    },
  },
  {
    path: "/funktionen/warnkarte",
    element: <Warnkarte />,
    head: {
      title: "Die Warnkarte für Haustiere | WeLovePets",
      description:
        "Giftköder, Fallen, aggressive Hunde und vermisste Tiere melden — die community-getriebene Warnkarte warnt Halter in deiner Nähe mit wählbarem Radius.",
      canonical: abs("/funktionen/warnkarte"),
      ogType: "website",
      jsonLd: [
        {
          "@type": "WebPage",
          name: "Die Warnkarte",
          description:
            "Community-getriebene Karte für Gefahren und vermisste Tiere: Giftköder, Fallen, aggressive Hunde, Straßengefahren, vermisste Tiere.",
          inLanguage: "de-DE",
          url: abs("/funktionen/warnkarte"),
          isPartOf: { "@type": "WebSite", name: "Welope", url: abs("/") },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Start", item: abs("/") },
            { "@type": "ListItem", position: 2, name: "Funktionen", item: abs("/#funktionen") },
            { "@type": "ListItem", position: 3, name: "Warnkarte", item: abs("/funktionen/warnkarte") },
          ],
        },
      ],
    },
  },
  {
    path: "/impressum",
    element: <Impressum />,
    head: {
      title: "Impressum | WeLovePets",
      description: "Impressum von Welope gemäß § 5 TMG: Anbieterkennzeichnung und Kontakt.",
      canonical: abs("/impressum"),
      ogType: "website",
      robots: "noindex,follow",
    },
    sitemap: false,
  },
  {
    path: "/datenschutz",
    element: <Datenschutz />,
    head: {
      title: "Datenschutzerklärung | WeLovePets",
      description:
        "Welche Daten Welope erhebt, wofür, wie lange gespeichert wird und welche Rechte Sie nach der DSGVO haben.",
      canonical: abs("/datenschutz"),
      ogType: "website",
      robots: "noindex,follow",
    },
    sitemap: false,
  },
  {
    path: "/agb",
    element: <Agb />,
    head: {
      title: "AGB | WeLovePets",
      description: "Allgemeine Geschäftsbedingungen und Nutzungsbedingungen von Welope.",
      canonical: abs("/agb"),
      ogType: "website",
      robots: "noindex,follow",
    },
    sitemap: false,
  },
  ...guides.map(guideRoute),
];

/** Normalize a pathname for matching (strip trailing slash except root). */
export function normalizePath(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

/** The home route is always index 0 and always exists. */
const homeRoute = routes[0] as RouteDef;

export function matchRoute(pathname: string): RouteDef {
  const normalized = normalizePath(pathname);
  return routes.find((r) => r.path === normalized) ?? homeRoute;
}
